import type {ReactElement} from 'react';
import React from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import {useGetFancyTextFonts} from '@Hooks/poster-editor/useGetFancyTextFonts';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {FancyTextType} from '@Components/fancy-text-list/fancy-text-list.types';
import {FancyTextList} from '@Components/fancy-text-list';
import {getFancyTextFontFamilyFromId, getIdFromFancyTextFontFamily} from '@Libraries/fancy-text-library';
import styles from './poster-editor-fancy-text-fonts-panel.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromFancyTextItem} from '@Hooks/poster-editor/useGetKeyFromFancyTextItem';

interface PosterEditorFancyTextFontsPanelProps {
  panelId: string;
}

export default function PosterEditorFancyTextFontsPanel({...props}: PosterEditorFancyTextFontsPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const {isLoading, fonts} = useGetFancyTextFonts();
  const itemFontFamily = useGetKeyFromFancyTextItem('fontFamily');

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  if (!activeItem?.isFancyText() || itemFontFamily === undefined) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_fonts')} />;
  };

  const getContent = (): ReactElement => {
    return (
      <div className={`${styles.container} ${isLoading ? 'flex-center' : ''} spacing-p-t-1 spacing-p-l-4 spacing-p-r-4 spacing-p-b-6 `}>
        {fonts !== null && !isLoading ? (
          <FancyTextList
            data={{
              type: FancyTextType.FONT,
              fancyTextFonts: fonts,
            }}
            onClick={(id: string): void => {
              const fontFamily = getFancyTextFontFamilyFromId(id);
              if (!fontFamily) {
                return;
              }
              if (itemFontFamily === fontFamily) {
                return;
              }

              void activeItem.updateFancyTextFont(fontFamily);
              onClose();
            }}
            selectedId={getIdFromFancyTextFontFamily(itemFontFamily)}
            doScrollIntoView
          />
        ) : (
          <CircularProgressLoader classes="spacing-m-0" size={LOADER_SIZE.DEFAULT} color={LOADER_COLOR.DARK} />
        )}
      </div>
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
